import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import { graphql } from '../../gql';
import styles from './ChecklistProgressCard.module.scss';
import { ReactComponent as ChecklistIcon } from '../../shared/icons/checklist-icon.svg';
import { ReactComponent as ChevronRightIcon } from '../../shared/icons/chevron-right.svg';

const GET_CHECKLISTS_BY_DEAL = graphql(/* GraphQL */ `
  query GetChecklistsByDealForCard($dealId: ID!) {
    checklistsByDeal(dealId: $dealId) {
      id
      completedCount
      totalCount
    }
  }
`);

interface ChecklistProgressCardProps {
  dealId: string;
  onClick: () => void;
}

export const ChecklistProgressCard: React.FC<ChecklistProgressCardProps> = ({ dealId, onClick }) => {
  const { data, loading } = useQuery(GET_CHECKLISTS_BY_DEAL, {
    variables: { dealId },
    skip: !dealId,
  });

  const checklists = data?.checklistsByDeal || [];
  const checklist = checklists[0]; // Single checklist per deal

  // Calculate progress for the single checklist
  const completedCount = checklist?.completedCount || 0;
  const totalCount = checklist?.totalCount || 0;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const buttonContent = loading ? (
    <>
      <div className={styles.leftSection}>
        <div className={styles.iconWrapper}>
          <ChecklistIcon className={styles.icon} />
        </div>
        <h3 className={styles.title}>
          <FormattedMessage id="checklist.title" defaultMessage="Checklist" />
        </h3>
      </div>
      <div className={styles.loadingText}>
        <FormattedMessage id="checklist.loading" defaultMessage="Loading..." />
      </div>
    </>
  ) : (
    <>
      <div className={styles.leftSection}>
        <div className={styles.iconWrapper}>
          <ChecklistIcon className={styles.icon} />
        </div>
        <h3 className={styles.title}>
          <FormattedMessage id="checklist.title" defaultMessage="Checklist" />
        </h3>
        <ChevronRightIcon className={styles.chevronIcon} />
      </div>

      {totalCount > 0 ? (
        <div className={styles.rightSection}>
          <div className={styles.progressBarContainer}>
            <div
              className={styles.progressBar}
              style={{ width: `${progressPercentage}%` }}
              role="progressbar"
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <span className={styles.progressPercentage}>{completedCount}/{totalCount}</span>
        </div>
      ) : (
        <div className={styles.emptyState}>
          <FormattedMessage id="checklist.empty" defaultMessage="No items yet" />
        </div>
      )}
    </>
  );

  return (
    <div className={styles.checklistCardWrapper}>
      <button className={styles.checklistCard} onClick={onClick} type="button" disabled={loading}>
        {buttonContent}
      </button>
    </div>
  );
};

export default ChecklistProgressCard;
